import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent {
  /**
   * Button variant determines the visual style
   * - filled: solid background color
   * - outline: transparent with colored border
   * - invisible: transparent with no border
   */
  @Input() variant: 'filled' | 'outline' | 'invisible' = 'filled';

  /**
   * Button color theme
   * - primary: main brand color
   * - secondary: complementary color
   * - error: for destructive actions
   * - light: light color suitable for dark backgrounds
   */
  @Input() color: 'primary' | 'secondary' | 'error' | 'light' = 'primary';

  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() block = false;
  @Input() icon = false;
  @Input() square = false;
  @Input() disabled = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() ariaLabel: string | null = null;

  /**
   * Link properties
   */
  @Input() href: string | null = null; // For regular anchor links
  @Input() routerLink: string | any[] | null = null; // For Angular router links
  @Input() target: string | null = null; // For anchor target (_blank, etc)
  @Input() rel: string | null = null; // For anchor rel attribute
  @Input() queryParamsHandling: 'merge' | 'preserve' | '' = ''; // Query params handling for router links

  /**
   * Element type to render
   * - button: standard button element
   * - a: anchor element
   * - router: router link element
   */
  get elementType(): 'button' | 'a' | 'router' {
    if (this.routerLink) return 'router';
    if (this.href) return 'a';
    return 'button';
  }

  @Output() btnClick = new EventEmitter<MouseEvent>();

  onClick(event: MouseEvent): void {
    if (!this.disabled) {
      this.btnClick.emit(event);
    }
  }
}
